import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Plus, X, Check, AlertCircle, AlertTriangle, Info,
  Send, FileText, Coins, Sparkles, Edit, MessageCircle, CheckCircle,
  Crown, Upload, Loader, Trash2,
} from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import {
  expensesApi,
  ExpenseDTO,
  ExpenseStatus,
  ExpenseCreatePayload,
} from '../api/expenses';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';
import { useDismissOnEscape } from '../hooks/useDismissOnEscape';
import { errorMessage } from '../utils/errors';
// Category/currency vocabulary is shared with the assistant's expense draft card.
import { CATEGORIES, findCategory, POPULAR_CURRENCIES } from '../constants/expenses';
import '../styles/expenses.css';

const VENDOR_PALETTE: [string, string][] = [
  ['#BE5540', '#571F15'], ['#1d4ed8', '#1e3a8a'], ['#15803d', '#14532d'],
  ['#c96b00', '#7c2d12'], ['#db2777', '#831843'], ['#0891b2', '#155e75'],
  ['#525866', '#1f2937'], ['#4338ca', '#312e81'], ['#b45309', '#78350f'],
];

// Two-state model for the UI. Legacy statuses (pending/approved/processing/sent)
// all read as "Unpaid".
type UiStatus = 'unpaid' | 'paid' | 'overdue';
const normalizeStatus = (s: string): UiStatus =>
  s === 'paid' ? 'paid' : s === 'overdue' ? 'overdue' : 'unpaid';
const STATUS_LABEL: Record<UiStatus, string> = { unpaid: 'Unpaid', paid: 'Paid', overdue: 'Overdue' };
// Reuse existing pill CSS (no `.unpaid` class exists, so map it to `.pending`).
const STATUS_PILL_CLASS: Record<UiStatus, string> = { unpaid: 'pending', paid: 'paid', overdue: 'overdue' };

const fmtMoney = (n: number, currency: string, compact = false) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  }).format(n);

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtDateLong = (s: string | null) => {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Names the rate day used for expenses that predate the 90-day rate history.
// Never says "today": the badge and note outlive the day they render on, and
// the ECB publishes on business days only.
const fxRateClause = (iso?: string | null) =>
  iso ? `the exchange rate of ${fmtDateLong(iso)}, the newest one available`
      : 'the newest exchange rate available';
const fxRateBadge = (iso?: string | null) => (iso ? `≈ ${fmtDate(iso)} rate` : '≈ latest rate');

const displayAmount = (e: ExpenseDTO) => e.amount_converted ?? e.amount;
const displayCurrency = (e: ExpenseDTO) => e.preferred_currency ?? e.currency;

// ---------- subcomponents ----------
const VendorMark: React.FC<{ vendor: string; short: string | null; idx?: number }> = ({ vendor, short, idx = 0 }) => {
  const [a, b] = VENDOR_PALETTE[idx % VENDOR_PALETTE.length];
  const label = short || vendor.slice(0, 2).toUpperCase();
  return (
    <div className="exp-vendor-mark" style={{ background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)` }}>
      {label}
    </div>
  );
};

const Checkbox: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <div
    className={`exp-check${checked ? ' checked' : ''}`}
    onClick={e => { e.stopPropagation(); onChange(!checked); }}
  >
    {checked && <Check size={12} />}
  </div>
);

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const ui = normalizeStatus(status);
  return <span className={`exp-pill ${STATUS_PILL_CLASS[ui]}`}>{STATUS_LABEL[ui]}</span>;
};

// ---------- KPI strip (computed client-side from the list — no extra API call) ----------
interface Kpis {
  thisMonth: number;
  unpaidCount: number; unpaidTotal: number;
  paidCount: number;   paidTotal: number;
  overdueCount: number; overdueTotal: number;
}

// Card label with a hover-info icon (native tooltip) explaining what it counts.
const KpiLabel: React.FC<{ text: string; info: string; light?: boolean }> = ({ text, info, light }) => (
  <div
    className="exp-kpi-label"
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, ...(light ? { color: 'rgba(255,255,255,0.75)' } : {}) }}
  >
    <span>{text}</span>
    <span title={info} aria-label={info} style={{ cursor: 'help', display: 'inline-flex', opacity: light ? 0.85 : 0.5 }}>
      <Info size={13} />
    </span>
  </div>
);

const KPIStrip: React.FC<{ kpis: Kpis; ccy: string; onReviewOverdue: () => void }> = ({ kpis, ccy, onReviewOverdue }) => (
  <div className="exp-kpi-grid">
    <div className="exp-kpi primary">
      <KpiLabel text="This month" info={`Total money out dated in the current calendar month — both paid and unpaid. Shown in ${ccy}.`} />
      <div className="exp-kpi-value" style={{ fontSize: 34 }}>{fmtMoney(kpis.thisMonth, ccy, true)}</div>
      <div className="exp-kpi-foot"><span>Money out dated this month</span></div>
    </div>

    <div className="exp-kpi">
      <KpiLabel text="Unpaid" info="What you still owe — every expense not marked paid, including overdue ones." />
      <div className="exp-kpi-value">{fmtMoney(kpis.unpaidTotal, ccy, true)}</div>
      <div className="exp-kpi-foot">
        <AlertCircle size={12} />
        {kpis.unpaidCount} {kpis.unpaidCount === 1 ? 'bill' : 'bills'} to pay
      </div>
    </div>

    <div className="exp-kpi">
      <KpiLabel text="Paid" info="Everything you've marked as paid (all time)." />
      <div className="exp-kpi-value">{fmtMoney(kpis.paidTotal, ccy, true)}</div>
      <div className="exp-kpi-foot">
        <CheckCircle size={12} />
        {kpis.paidCount} {kpis.paidCount === 1 ? 'payment' : 'payments'}
      </div>
    </div>

    <div className="exp-kpi danger">
      <KpiLabel text="Overdue" info="Unpaid expenses that are past their due date." />
      <div className="exp-kpi-value">{fmtMoney(kpis.overdueTotal, ccy, true)}</div>
      <div className="exp-kpi-foot" style={{ color: 'var(--color-error)' }}>
        <AlertTriangle size={12} />
        {kpis.overdueCount} past due
      </div>
      {kpis.overdueCount > 0 && (
        <button className="exp-quick primary" style={{ marginTop: 'auto', alignSelf: 'flex-start' }} onClick={onReviewOverdue}>
          <Send size={11} />Review overdue
        </button>
      )}
    </div>
  </div>
);

// ---------- category breakdown ----------
const CategoryBreakdown: React.FC<{ data: ExpenseDTO[] }> = ({ data }) => {
  const ccy = data[0]?.preferred_currency || 'USD';
  const totals = useMemo(() => {
    const map: Record<string, number> = {};
    data.forEach(e => {
      const cat = e.category || 'other';
      map[cat] = (map[cat] || 0) + displayAmount(e);
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0) || 1;
    return CATEGORIES
      .map(c => ({ ...c, amount: map[c.id] || 0, pct: ((map[c.id] || 0) / total) * 100 }))
      .filter(c => c.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [data]);

  return (
    <div className="exp-card">
      <div className="exp-card-head">
        <h3>By category</h3>
      </div>
      <div className="exp-card-body" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {totals.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-foreground-muted)', padding: '12px 0' }}>
            No categorised expenses yet.
          </div>
        )}
        {totals.slice(0, 6).map(c => (
          <div key={c.id} className="exp-cat-row">
            <span className="exp-cat-dot" style={{ background: c.color }} />
            <div style={{ minWidth: 0 }}>
              <div className="exp-cat-name">{c.name}</div>
              <div className="exp-cat-bar">
                <div className="exp-cat-fill" style={{ width: `${c.pct}%`, background: c.color }} />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="exp-cat-amount">{fmtMoney(c.amount, ccy, true)}</div>
              <div className="exp-cat-pct">{c.pct.toFixed(0)}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- filter bar ----------
const STATUS_FILTERS: { id: 'all' | UiStatus; label: string }[] = [
  { id: 'all',     label: 'All' },
  { id: 'unpaid',  label: 'Unpaid' },
  { id: 'paid',    label: 'Paid' },
  { id: 'overdue', label: 'Overdue' },
];

interface FilterBarProps {
  search: string;
  setSearch: (v: string) => void;
  statusFilter: 'all' | UiStatus;
  setStatusFilter: (v: 'all' | UiStatus) => void;
  counts: Record<string, number>;
  catFilter: string;
  setCatFilter: (v: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ search, setSearch, statusFilter, setStatusFilter, counts, catFilter, setCatFilter }) => (
  <div className="exp-filterbar">
    <div className="exp-search">
      <Search size={16} className="exp-search-icon" />
      <input
        placeholder="Search who you paid, or notes…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
    </div>
    {STATUS_FILTERS.map(f => (
      <div
        key={f.id}
        className={`exp-filter-pill${statusFilter === f.id ? ' active' : ''}`}
        onClick={() => setStatusFilter(f.id)}
      >
        {f.label}
        <span className="count">{counts[f.id] || 0}</span>
      </div>
    ))}
    <div style={{ width: 1, height: 24, background: 'var(--color-card-border)', margin: '0 4px' }} />
    <select className="exp-cat-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
      <option value="all">All categories</option>
      {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  </div>
);

// ---------- table row ----------
const ExpenseRow: React.FC<{
  exp: ExpenseDTO;
  idx: number;
  selected: boolean;
  onSelect: () => void;
  onClick: () => void;
}> = ({ exp, idx, selected, onSelect, onClick }) => {
  const c = findCategory(exp.category);
  const shownCurrency = displayCurrency(exp);
  const isConverted = exp.preferred_currency && exp.currency !== exp.preferred_currency;
  return (
    <tr className={selected ? 'selected' : ''} onClick={onClick}>
      <td style={{ width: 36 }}>
        <Checkbox checked={selected} onChange={onSelect} />
      </td>
      <td>
        <div className="exp-vendor">
          <VendorMark vendor={exp.vendor || '—'} short={exp.short} idx={idx} />
          <div>
            <div className="exp-vendor-name">
              {exp.vendor || 'Untitled expense'}
              {exp.source === 'document' && (
                <span
                  title={
                    exp.read_only
                      ? 'Uploaded invoice marked as money out — managed in Uploaded Invoices'
                      : 'Started from an uploaded invoice, now tracked as its own expense — still linked to the original'
                  }
                  style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
                    color: 'var(--color-primary)', background: 'var(--color-primary-muted)',
                    padding: '1px 5px', borderRadius: 4, verticalAlign: 1,
                  }}
                >
                  <Upload size={9} style={{ verticalAlign: -1, marginRight: 2 }} />
                  {exp.read_only ? 'UPLOADED' : 'UPLOADED · EDITED'}
                </span>
              )}
            </div>
            <div className="exp-vendor-id">{exp.invoice || exp.id}</div>
          </div>
        </div>
      </td>
      <td>
        <span className="exp-cat-chip">
          <span className="dot" style={{ background: c.color }} />
          {c.name}
        </span>
      </td>
      <td style={{ color: 'var(--color-foreground-secondary)', fontSize: 13 }}>{fmtDate(exp.date)}</td>
      <td style={{ color: 'var(--color-foreground-secondary)', fontSize: 13 }}>
        {fmtDate(exp.due)}
        {normalizeStatus(exp.status) === 'overdue' && (
          <span style={{ color: 'var(--color-error)', marginLeft: 6, fontWeight: 600, fontSize: 11 }}>OVERDUE</span>
        )}
      </td>
      <td><StatusPill status={exp.status} /></td>
      <td style={{ textAlign: 'right' }}>
        <div className="exp-amount" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {exp.fx_fallback && (
            <span
              title={
                `Converted at ${fxRateClause(exp.fx_rate_date)} — this expense is older than the ` +
                '90-day rate window, so the rate from its own date is unavailable. The conversion ' +
                'is redone with the newest rate each time this page loads.'
              }
              style={{
                fontSize: 10, fontWeight: 600, color: 'var(--color-warning)',
                background: 'var(--color-warning-bg)', padding: '1px 5px',
                borderRadius: 4, letterSpacing: '0.04em', whiteSpace: 'nowrap',
              }}
            >
              {fxRateBadge(exp.fx_rate_date)}
            </span>
          )}
          {fmtMoney(displayAmount(exp), shownCurrency)}
        </div>
        {isConverted && (
          <div style={{ fontSize: 11, color: 'var(--color-foreground-muted)', fontVariantNumeric: 'tabular-nums' }}>
            {fmtMoney(exp.amount, exp.currency)}
          </div>
        )}
      </td>
    </tr>
  );
};

// ---------- detail drawer ----------
const DetailDrawer: React.FC<{
  exp: ExpenseDTO | null;
  onClose: () => void;
  onEdit: (e: ExpenseDTO) => void;
  onStatus: (e: ExpenseDTO, status: ExpenseStatus) => void;
  onDelete: (e: ExpenseDTO) => void;
  onAskAI: (e: ExpenseDTO) => void;
  onOpenDocument: (e: ExpenseDTO) => void;
  busy: boolean;
}> = ({ exp, onClose, onEdit, onStatus, onDelete, onAskAI, onOpenDocument, busy }) => {
  const open = !!exp;
  const c = exp ? findCategory(exp.category) : null;
  const shownCurrency = exp ? displayCurrency(exp) : 'USD';

  // The drawer already closed on a backdrop click; Escape now works too, so its
  // dismissal matches every other overlay.
  useDismissOnEscape(open, onClose);

  return (
    <>
      <div className={`exp-drawer-overlay${open ? ' open' : ''}`} onClick={onClose} />
      <div className={`exp-drawer${open ? ' open' : ''}`}>
        {exp && c && (
          <>
            <div className="exp-drawer-head">
              <VendorMark vendor={exp.vendor || '—'} short={exp.short} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--color-foreground-muted)', fontFamily: 'ui-monospace, monospace' }}>
                  {exp.id}
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2, color: 'rgb(var(--text-primary))' }}>
                  {exp.vendor || 'Untitled expense'}
                </h2>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusPill status={exp.status} />
                  <span className="exp-cat-chip">
                    <span className="dot" style={{ background: c.color }} />
                    {c.name}
                  </span>
                </div>
              </div>
              <button className="exp-btn icon-btn" onClick={onClose}><X size={18} /></button>
            </div>

            <div className="exp-drawer-body">
              <div className="exp-section-head">
                <FileText size={12} />Details
              </div>
              <div className="exp-field-grid">
                <div className="exp-field">
                  <div className="exp-field-label">Amount</div>
                  <div className="exp-field-value">{fmtMoney(exp.amount, exp.currency)}</div>
                </div>
                <div className="exp-field">
                  <div className="exp-field-label">In {shownCurrency}</div>
                  <div className="exp-field-value" style={{ color: 'var(--color-primary)', fontWeight: 700 }}>
                    {fmtMoney(displayAmount(exp), shownCurrency)}
                  </div>
                </div>
                <div className="exp-field">
                  <div className="exp-field-label">Date</div>
                  <div className="exp-field-value">{fmtDateLong(exp.date)}</div>
                </div>
                <div className="exp-field">
                  <div className="exp-field-label">Due date</div>
                  <div className="exp-field-value">{fmtDateLong(exp.due)}</div>
                </div>
              </div>

              {exp.notes && (
                <>
                  <div className="exp-section-head"><MessageCircle size={12} />Notes</div>
                  <div style={{ fontSize: 13, color: 'var(--color-foreground-secondary)', lineHeight: 1.5 }}>{exp.notes}</div>
                </>
              )}

              {exp.preferred_currency && exp.currency !== exp.preferred_currency && (
                <div className={`exp-alert ${exp.fx_fallback ? 'warn' : 'info'}`} style={{ marginTop: 16 }}>
                  {exp.fx_fallback ? <AlertTriangle size={16} /> : <Sparkles size={16} />}
                  <div>
                    {exp.fx_fallback ? (
                      <>
                        Converted at <b>{fxRateClause(exp.fx_rate_date)}</b>. This expense ({fmtDateLong(exp.date)}) is older than the 90-day rate history, so the rate from its own date isn't available. The conversion is redone every time this page loads, so the figure moves as rates change; the original {exp.currency} amount is kept above.
                      </>
                    ) : (
                      <>
                        Converted using the <b>exchange rate on {fmtDateLong(exp.date)}</b>. The original amount in {exp.currency} is kept.
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="exp-drawer-foot">
              <button className="exp-btn ghost" onClick={() => onAskAI(exp)}>
                <MessageCircle size={14} />Ask ViewPoint
              </button>
              {exp.read_only ? (
                // Read-only rows are synthesised from an uploaded invoice and have
                // no row of their own. Editing one saves a real, linked expense,
                // which then replaces it in the list.
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="exp-btn ghost" onClick={() => onOpenDocument(exp)}>
                    <FileText size={14} />Open uploaded invoice
                  </button>
                  <button className="exp-btn secondary" onClick={() => onEdit(exp)} disabled={busy}>
                    <Edit size={14} />Edit
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* Kept for expenses materialised from an upload — the link back
                      to the original invoice is still useful once it's editable. */}
                  {exp.source === 'document' && (
                    <button className="exp-btn ghost" onClick={() => onOpenDocument(exp)} title="Open the invoice this came from">
                      <FileText size={14} />Invoice
                    </button>
                  )}
                  <button className="exp-btn ghost" onClick={() => onDelete(exp)} disabled={busy} title="Delete expense">
                    <Trash2 size={14} />
                  </button>
                  <button className="exp-btn secondary" onClick={() => onEdit(exp)} disabled={busy}>
                    <Edit size={14} />Edit
                  </button>
                  {normalizeStatus(exp.status) !== 'paid' && (
                    <button className="exp-btn primary" onClick={() => onStatus(exp, 'paid')} disabled={busy}>
                      {busy ? <Loader size={14} className="animate-spin" /> : <Coins size={14} />}Mark paid
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ---------- add / edit expense (one simple form) ----------
const prefillForm = (e: ExpenseDTO): Record<string, any> => ({
  vendor: e.vendor || '',
  amount: e.amount != null ? String(e.amount) : '',
  currency: e.currency || 'USD',
  category: e.category || 'other',
  date: e.date || '',
  due: e.due || '',
  notes: e.notes || '',
  paid: e.status === 'paid',
});

const blankForm = (): Record<string, any> => ({ currency: 'USD', category: 'other', paid: true });

const AddExpenseModal: React.FC<{ open: boolean; editing: ExpenseDTO | null; onClose: () => void; onSaved: () => void }> = ({ open, editing, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>(blankForm);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? prefillForm(editing) : blankForm());
    setError(null);
  }, [open, editing]);

  useDismissOnEscape(open, onClose);

  if (!open) return null;

  // A read-only row has no expense of its own — it is rendered from an uploaded
  // invoice. "Editing" it means creating a real expense linked to that document,
  // which makes the synthesised row disappear from the list.
  const materializing = !!editing?.read_only;

  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    const amount = parseFloat(form.amount || '0');
    if (!isFinite(amount) || amount <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    if (!form.vendor || !String(form.vendor).trim()) {
      setError('Add who you paid so you can recognise this expense later.');
      return;
    }

    const paid = !!form.paid;
    setSubmitting(true);
    try {
      const payload: ExpenseCreatePayload = {
        expense_type: materializing ? 'invoice' : 'manual',
        vendor: String(form.vendor).trim(),
        category: form.category || 'other',
        amount,
        currency: (form.currency || 'USD').toUpperCase(),
        date: form.date || undefined,
        due: !paid && form.due ? form.due : undefined,
        status: paid ? 'paid' : 'unpaid',
        notes: form.notes ? String(form.notes).trim() : undefined,
        ...(materializing && {
          source_document_id: editing?.source_document_id ?? undefined,
          invoice: editing?.invoice ?? undefined,
        }),
      };
      if (editing && !materializing) {
        await expensesApi.update(editing.db_id, payload);
      } else {
        await expensesApi.create(payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="exp-modal-overlay" onClick={onClose}>
      <div className="exp-modal" onClick={e => e.stopPropagation()}>
        <div className="exp-modal-head">
          <div>
            <h2>{materializing ? 'Track this invoice' : editing ? 'Edit expense' : 'Add expense'}</h2>
            <div className="sub">
              {materializing
                ? 'These details came from your uploaded invoice. Saving creates a tracked expense linked to it, so you can set the category and mark it paid.'
                : 'Record any money that left your account — a transfer, a tax bill, a subscription, anything.'}
            </div>
          </div>
          <button className="exp-btn icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="exp-modal-body">
          {error && (
            <div className="exp-alert warn" style={{ marginBottom: 16 }}>
              <AlertTriangle size={16} />
              <div>{error}</div>
            </div>
          )}

          <div className="exp-form-grid">
            <div className="exp-form-field full">
              <label>Who you paid</label>
              <input value={form.vendor || ''} onChange={e => setF('vendor', e.target.value)} placeholder="e.g. Dropbox, the tax office, your accountant" />
            </div>
            <div className="exp-form-field">
              <label>Amount</label>
              <input inputMode="decimal" value={form.amount || ''} onChange={e => setF('amount', e.target.value)} placeholder="120.00" />
            </div>
            <div className="exp-form-field">
              <label>Currency</label>
              <select value={form.currency || 'USD'} onChange={e => setF('currency', e.target.value)}>
                {POPULAR_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="exp-form-field">
              <label>Date</label>
              <input type="date" value={form.date || ''} onChange={e => setF('date', e.target.value)} />
            </div>
            <div className="exp-form-field">
              <label>Category</label>
              <select value={form.category || 'other'} onChange={e => setF('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="exp-form-field full">
              <label>Have you paid it?</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className={`exp-filter-pill${form.paid ? ' active' : ''}`} onClick={() => setF('paid', true)}>Paid</button>
                <button type="button" className={`exp-filter-pill${!form.paid ? ' active' : ''}`} onClick={() => setF('paid', false)}>Not yet</button>
              </div>
            </div>
            {!form.paid && (
              <div className="exp-form-field">
                <label>Due date (optional)</label>
                <input type="date" value={form.due || ''} onChange={e => setF('due', e.target.value)} />
              </div>
            )}
            <div className="exp-form-field full">
              <label>Notes (optional)</label>
              <textarea rows={2} value={form.notes || ''} onChange={e => setF('notes', e.target.value)} placeholder="What was this for?" />
            </div>
          </div>
        </div>

        <div className="exp-modal-foot">
          <span style={{ fontSize: 12, color: 'var(--color-foreground-muted)' }}>
            Saved in its original currency · totals use the exchange rate on the expense date.
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="exp-btn secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="exp-btn primary" onClick={submit} disabled={submitting}>
              {submitting ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
              {materializing ? 'Save expense' : editing ? 'Save changes' : 'Add expense'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- gated empty state ----------
const ExpensesGate: React.FC = () => (
  <div className="exp-container">
    <div className="exp-gate">
      <div className="exp-gate-icon"><Crown size={28} /></div>
      <h2>Expenses is available on the Discovery and Advanced plans</h2>
      <p>
        Keep track of every payment that leaves your account — transfers, taxes,
        subscriptions, bills and more — with totals shown in the currency you choose,
        using the exchange rate on each expense's date.
      </p>
      <p style={{ marginTop: 8 }}>
        On your current plan you can still <Link to="/uploaded-invoices" style={{ color: 'var(--color-primary)' }}>upload an invoice</Link> and
        mark it as money out. Upgrade to add anything else here and see it all in one place.
      </p>
      <Link to="/pricing" className="exp-btn primary" style={{ textDecoration: 'none' }}>
        <Crown size={14} />View plans
      </Link>
    </div>
  </div>
);

// ---------- reporting currency picker ----------
const CurrencyPicker: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 12, color: 'var(--color-foreground-muted)' }}>Show totals in</span>
    <select className="exp-cat-select" value={value} onChange={e => onChange(e.target.value)} style={{ fontWeight: 600 }}>
      {POPULAR_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  </div>
);

// ---------- main page ----------
const ExpensesPage: React.FC = () => {
  const { subscription, loading: subLoading, isExpensesEligible, planKnown } = useSubscription();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | UiStatus>('all');
  const [catFilter, setCatFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<ExpenseDTO | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseDTO | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [expenses, setExpenses] = useState<ExpenseDTO[]>([]);
  const [preferredCurrency, setPreferredCurrency] = useState<string>('USD');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  const reload = useCallback(async () => {
    setLoadingData(true);
    setLoadError(null);
    try {
      const listRes = await expensesApi.list();
      setExpenses(listRes.expenses);
      setPreferredCurrency(listRes.preferred_currency);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load expenses');
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!isExpensesEligible) return;
    reload();
  }, [isExpensesEligible, reload]);

  const handleCurrencyChange = async (code: string) => {
    const prev = preferredCurrency;
    setPreferredCurrency(code);
    try {
      await expensesApi.setPreferredCurrency(code);
      reload();
    } catch (e: any) {
      setPreferredCurrency(prev); // revert so the picker reflects reality
      setLoadError(e?.message || 'Failed to update the reporting currency');
    }
  };

  // ---- mutations (all hit the real API, then refresh) ----
  const changeStatus = async (exp: ExpenseDTO, status: ExpenseStatus) => {
    setActionBusy(true);
    try {
      await expensesApi.update(exp.db_id, { status });
      setDrawer(null);
      await reload();
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to update expense');
    } finally {
      setActionBusy(false);
    }
  };

  const removeExpense = async (exp: ExpenseDTO) => {
    const ok = await confirm({
      title: 'Delete this expense?',
      message: (
        <>
          {exp.vendor ? <><b>{exp.vendor}</b> — </> : null}
          this expense will be permanently removed. This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete expense',
      destructive: true,
    });
    if (!ok) return;
    setActionBusy(true);
    try {
      await expensesApi.remove(exp.db_id);
      setDrawer(null);
      setSelected(prev => { const n = new Set(prev); n.delete(exp.id); return n; });
      toast.success('Expense deleted.');
      await reload();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to delete the expense.'));
    } finally {
      setActionBusy(false);
    }
  };

  const openEdit = (exp: ExpenseDTO) => {
    setEditing(exp);
    setDrawer(null);
    setAddOpen(true);
  };

  const askAI = (exp: ExpenseDTO) => {
    navigate('/ai', {
      state: {
        prefillMessage: `Tell me about this expense: ${exp.vendor || 'Untitled'}`,
        contextData: {
          vendor: exp.vendor,
          amount: exp.amount,
          currency: exp.currency,
          amount_converted: exp.amount_converted,
          preferred_currency: exp.preferred_currency,
          status: exp.status,
          category: exp.category,
          date: exp.date,
          due: exp.due,
        },
        source: 'expenses',
      },
    });
  };

  const openDocument = (exp: ExpenseDTO) => {
    if (exp.source_document_id) navigate(`/uploaded-invoices?documentId=${exp.source_document_id}`);
  };

  // Rows synthesised from an uploaded document are not editable expense records —
  // they are owned by the document. Both bulk actions used to filter them out and
  // return early, so selecting only those rows cleared the selection and did
  // nothing at all. Say so instead.
  //
  // Keyed on read_only, not source: an uploaded invoice that has been edited into
  // a real expense keeps source 'document' as provenance but is fully editable.
  const bulkTargets = (predicate: (e: ExpenseDTO) => boolean) => {
    const chosen = expenses.filter(e => selected.has(e.id));
    return {
      targets: chosen.filter(e => !e.read_only && predicate(e)),
      documentBacked: chosen.filter(e => e.read_only).length,
    };
  };

  const markPaidSelected = async () => {
    const { targets, documentBacked } = bulkTargets(e => normalizeStatus(e.status) !== 'paid');
    if (targets.length === 0) {
      toast.warning(
        documentBacked > 0
          ? 'Expenses from uploaded documents are managed on the document itself and can’t be marked paid here.'
          : 'Nothing to update — the selected expenses are already paid.'
      );
      return;
    }
    setActionBusy(true);
    try {
      await Promise.all(targets.map(e => expensesApi.update(e.db_id, { status: 'paid' })));
      setSelected(new Set());
      toast.success(
        documentBacked > 0
          ? `Marked ${targets.length} paid. ${documentBacked} document-backed row(s) were skipped.`
          : `Marked ${targets.length} expense${targets.length === 1 ? '' : 's'} paid.`
      );
      await reload();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to update the selected expenses.'));
    } finally {
      setActionBusy(false);
    }
  };

  const deleteSelected = async () => {
    const { targets, documentBacked } = bulkTargets(() => true);
    if (targets.length === 0) {
      toast.warning(
        documentBacked > 0
          ? 'Expenses from uploaded documents can’t be deleted here — delete the document instead.'
          : 'Select at least one expense to delete.'
      );
      return;
    }
    const ok = await confirm({
      title: `Delete ${targets.length} expense${targets.length === 1 ? '' : 's'}?`,
      message: (
        <>
          This cannot be undone.
          {documentBacked > 0 && (
            <> {documentBacked} row{documentBacked === 1 ? '' : 's'} from uploaded documents will be
            left untouched.</>
          )}
        </>
      ),
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setActionBusy(true);
    try {
      await Promise.all(targets.map(e => expensesApi.remove(e.db_id)));
      setSelected(new Set());
      toast.success(`Deleted ${targets.length} expense${targets.length === 1 ? '' : 's'}.`);
      await reload();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to delete the selected expenses.'));
    } finally {
      setActionBusy(false);
    }
  };

  const filtered = useMemo(() => {
    return expenses.filter(e => {
      if (statusFilter !== 'all' && normalizeStatus(e.status) !== statusFilter) return false;
      if (catFilter !== 'all' && e.category !== catFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [e.vendor, e.invoice, e.id, String(e.amount), e.notes].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [expenses, search, statusFilter, catFilter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: expenses.length };
    expenses.forEach(e => { const s = normalizeStatus(e.status); c[s] = (c[s] || 0) + 1; });
    return c;
  }, [expenses]);

  // KPIs computed from the (already converted) list — no separate /summary call.
  const kpis = useMemo<Kpis>(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const k: Kpis = {
      thisMonth: 0, unpaidCount: 0, unpaidTotal: 0,
      paidCount: 0, paidTotal: 0, overdueCount: 0, overdueTotal: 0,
    };
    for (const e of expenses) {
      const amt = displayAmount(e);
      if (e.date && new Date(e.date + 'T00:00:00') >= monthStart) k.thisMonth += amt;
      const st = normalizeStatus(e.status);
      if (st === 'paid') { k.paidCount++; k.paidTotal += amt; }
      else { k.unpaidCount++; k.unpaidTotal += amt; }      // unpaid includes overdue
      if (st === 'overdue') { k.overdueCount++; k.overdueTotal += amt; }
    }
    return k;
  }, [expenses]);

  // Count of rows converted outside the rate window, plus the rate day they used
  // (identical for every such row — it's whatever rate is newest right now).
  const { fxFallbackCount, fxRateDate } = useMemo(() => {
    const fallbacks = expenses.filter(e => e.fx_fallback);
    return {
      fxFallbackCount: fallbacks.length,
      fxRateDate: fallbacks.find(e => e.fx_rate_date)?.fx_rate_date ?? null,
    };
  }, [expenses]);

  if (subLoading && !subscription) {
    return (
      <div className="exp-container">
        <div style={{ height: 200 }} />
      </div>
    );
  }

  // Only show the paywall when we actually know the plan. A failed
  // /api/subscription/status request used to look identical to "not eligible",
  // so an Advanced customer whose request errored was told to upgrade.
  if (!planKnown) {
    return (
      <div className="exp-container">
        <div className="exp-empty">
          <p>We couldn’t load your plan, so Expenses is unavailable right now.</p>
          <button className="exp-btn" onClick={() => window.location.reload()}>Try again</button>
        </div>
      </div>
    );
  }

  if (!isExpensesEligible) {
    return <ExpensesGate />;
  }

  return (
    <>
      <div className="exp-container">
        <div className="exp-header">
          <div>
            <div className="exp-eyebrow"><span className="dot" />Money out · transfers, taxes, subscriptions and bills</div>
            <h1>Expenses</h1>
            <div className="sub">Every payment that leaves your account, in one place.</div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <CurrencyPicker value={preferredCurrency} onChange={handleCurrencyChange} />
            <button className="exp-btn primary" onClick={() => { setEditing(null); setAddOpen(true); }}>
              <Plus size={14} />Add expense
            </button>
          </div>
        </div>

        {loadError && (
          <div className="exp-alert warn" style={{ marginBottom: 16 }}>
            <AlertTriangle size={16} />
            <div>{loadError}</div>
          </div>
        )}

        {fxFallbackCount > 0 && (
          <div className="exp-alert info" style={{ marginBottom: 16 }}>
            <Info size={16} />
            <div>
              <b>{fxFallbackCount}</b> expense{fxFallbackCount === 1 ? '' : 's'} older than 90 days {fxFallbackCount === 1 ? 'was' : 'were'} converted at <b>{fxRateClause(fxRateDate)}</b> — the rate history only covers the last 90 days. These conversions are redone at the newest rate each time this page loads, so the converted totals move as rates change. Affected rows show a <span style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)', padding: '0 4px', borderRadius: 3, fontSize: 11, fontWeight: 600 }}>{fxRateBadge(fxRateDate)}</span> badge.
            </div>
          </div>
        )}

        <KPIStrip kpis={kpis} ccy={preferredCurrency} onReviewOverdue={() => setStatusFilter('overdue')} />

        <div style={{ marginBottom: 20 }}>
          <CategoryBreakdown data={expenses} />
        </div>

        {selected.size > 0 && (
          <div className="exp-selection-bar">
            <span className="count">{selected.size} selected</span>
            <span style={{ flex: 1, color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
              ·  Total {fmtMoney(
                Array.from(selected).reduce((s, id) => {
                  const e = expenses.find(x => x.id === id);
                  return s + (e?.amount_converted ?? e?.amount ?? 0);
                }, 0),
                preferredCurrency,
                true,
              )}
            </span>
            <button onClick={deleteSelected} disabled={actionBusy}>
              <Trash2 size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Delete
            </button>
            <button className="primary" onClick={markPaidSelected} disabled={actionBusy}>
              {actionBusy ? <Loader size={13} className="animate-spin" style={{ verticalAlign: -2, marginRight: 4 }} /> : <Check size={13} style={{ verticalAlign: -2, marginRight: 4 }} />}
              Mark paid
            </button>
            <button onClick={() => setSelected(new Set())} title="Clear" style={{ padding: '6px 8px' }}>
              <X size={14} />
            </button>
          </div>
        )}

        <FilterBar
          search={search} setSearch={setSearch}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          counts={counts}
          catFilter={catFilter} setCatFilter={setCatFilter}
        />

        <div className="exp-table-wrap">
          <table className="exp-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every(e => selected.has(e.id))}
                    onChange={() => {
                      const allSel = filtered.length > 0 && filtered.every(e => selected.has(e.id));
                      setSelected(allSel ? new Set() : new Set(filtered.map(e => e.id)));
                    }}
                  />
                </th>
                <th>Who you paid</th>
                <th>Category</th>
                <th>Date</th>
                <th>Due</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {loadingData ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 60, color: 'var(--color-foreground-muted)' }}>
                  <Loader size={20} style={{ verticalAlign: 'middle' }} /> Loading expenses…
                </td></tr>
              ) : (loadError && filtered.length === 0) ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 60, color: 'var(--color-error)' }}>
                  Couldn't load your expenses.
                  <button className="exp-btn secondary" style={{ marginLeft: 10 }} onClick={reload}>Retry</button>
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 60, color: 'var(--color-foreground-muted)' }}>
                  {expenses.length === 0 ? 'No expenses yet. Click Add expense to get started.' : 'No expenses match your filters.'}
                </td></tr>
              ) : filtered.map((exp, idx) => (
                <ExpenseRow
                  key={exp.id}
                  exp={exp}
                  idx={idx}
                  selected={selected.has(exp.id)}
                  onSelect={() => {
                    const n = new Set(selected);
                    if (n.has(exp.id)) n.delete(exp.id); else n.add(exp.id);
                    setSelected(n);
                  }}
                  onClick={() => setDrawer(exp)}
                />
              ))}
            </tbody>
          </table>
          <div className="exp-pagination">
            <div className="info">Showing {filtered.length} of {expenses.length} expenses</div>
          </div>
        </div>
      </div>

      <DetailDrawer
        exp={drawer}
        onClose={() => setDrawer(null)}
        onEdit={openEdit}
        onStatus={changeStatus}
        onDelete={removeExpense}
        onAskAI={askAI}
        onOpenDocument={openDocument}
        busy={actionBusy}
      />
      <AddExpenseModal
        open={addOpen}
        editing={editing}
        onClose={() => { setAddOpen(false); setEditing(null); }}
        onSaved={reload}
      />
    </>
  );
};

export default ExpensesPage;
